<template>
	<div class="reservation-confirmation">
		<div class="thanks">{{ getString('thanks_for_your_reservation') }}</div>
		<div class="customer-details">
			<div class="name">{{ reservationsStore.customerData.name }}</div>
			<div class="email">{{ reservationsStore.customerData.email }}</div>
		</div>
		<div class="date-details">
			<div class="date-icon" v-html="getIcon('iconCalendar')"></div>
			<div class="date-row">
				<div class="date-part">{{ formattedDate }}</div>
				<div class="time-part">{{ formattedTime }}</div>
			</div>
		</div>
		<div class="people-details">
			<div class="people-icon" v-html="getIcon('iconUser')"></div>
			<div class="people-row">
				<div class="people-part">{{ "x"+reservationsStore.chosenPeople+(reservationsStore.chosenPeople > 1 ? " "+getString('people') : " "+getString('person')) }}</div>
			</div>
		</div>
		<div class="actions-container">
			<MenuButton :title="getString('add_to_calendar')" @click="addToCalendar" />
			<MenuButton :title="getString('share_reservation')" outline @click="shareReservation" />
		</div>
	</div>
</template>

<script>
import MenuButton from "@/new-menu/components/generic/MenuButton.vue";
import {useLanguageStore, useNotificationStore, useScriptStore, useSvgStore} from "@/shared/stores";
import {useMenusStore} from "@/new-menu/stores";
import {useReservationsStore} from "@/book/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const menusStore = useMenusStore();
		const reservationsStore = useReservationsStore();
		const notificationStore = useNotificationStore();
		const svgStore = useSvgStore();
		const scriptStore = useScriptStore();
		return {getString: languageStore.getString, languageStore, reservationsStore, menusStore, notificationStore, getIcon: svgStore.get, scriptStore};
	},
	name: "ReservationConfirmation",
	components: {MenuButton},
	computed: {
		formattedDate() {
			return this.reservationsStore.chosenDate.toLocaleDateString([this.languageStore.lang || 'en-US'], {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
		},
		formattedTime() {
			if(this.endTime) {
				return this.reservationsStore.chosenTime.time+" - "+this.endTime.getHours().toString().padStart(2, "0")+":"+this.endTime.getMinutes().toString().padStart(2, "0");
			} else {
				return this.getString("at_time")+" "+this.reservationsStore.chosenTime.time;
			}
		},
		endTime() {
			let slot = this.reservationsStore.getSlotFromId(this.reservationsStore.chosenTime.slotId);
			if(slot && slot.time) {
				let startDateTime = new Date(this.reservationsStore.chosenDate);
				startDateTime.setHours(this.reservationsStore.chosenTime.time.split(":")[0]);
				startDateTime.setMinutes(this.reservationsStore.chosenTime.time.split(":")[1]);
				let endDateTime = new Date(startDateTime);
				endDateTime.setMinutes(endDateTime.getMinutes() + slot.time);
				return endDateTime;
			} else {
				return null;
			}
		}
	},
	methods: {
		shareReservation() {
			if(navigator && navigator.share) {

				navigator.share({
					title: this.getString('reservation_at')+" "+this.menusStore.business.name,
					text: this.getString('reservation_at_message', [this.menusStore.business.name, this.formattedDate, this.formattedTime, this.reservationsStore.chosenPeople, (this.reservationsStore.chosenPeople > 1 ? this.getString('people') : this.getString('person'))]),
					url: window.location.href
				}).catch(() => {
					this.notificationStore.showNotification("share_not_supported");
				});

			} else {
				this.notificationStore.showNotification("share_not_supported");
			}
		},
		addToCalendar() {

			this.scriptStore.loadScript("ics", () => {
				const cal = ics();
				const description = this.getString('reservation_at_message', [this.menusStore.business.name, this.formattedDate, this.formattedTime, this.reservationsStore.chosenPeople, (this.reservationsStore.chosenPeople > 1 ? this.getString('people') : this.getString('person'))]);
				const dateStart = new Date(this.reservationsStore.chosenDate);
				dateStart.setHours(this.reservationsStore.chosenTime.time.split(":")[0]);
				dateStart.setMinutes(this.reservationsStore.chosenTime.time.split(":")[1]);
				const dateEnd = this.endTime ? this.endTime : new Date(dateStart.getTime());
				console.log("addToCalendar", dateStart, dateEnd);
				cal.addEvent(this.menusStore.business.name, description, this.menusStore.business.address ? this.menusStore.business.address.address : this.menusStore.business.name, dateStart, dateEnd);
				cal.download(this.menusStore.business.name+" #"+this.reservationsStore.confirmedReservationId);
			});

		}
	}
}
</script>

<style scoped lang="scss">
.reservation-confirmation {
	display: flex;
	flex-direction: column;
	background-color: var(--theme-bg2-color);
	width: 100%;
	border-radius: var(--rounded-corner);
	padding: var(--padding-vertical) var(--padding-horizontal);

	.thanks {
		color: var(--theme-txt1-color);
		font-size: var(--font-size-xl);
		font-weight: 500;
	}

	.customer-details {
		gap: var(--padding-vertical);
		margin-top: var(--padding-vertical);

		.name, .email {
			color: var(--theme-txt2-color);
			font-size: var(--font-size);
		}
	}

	.date-details {
		display: flex;
		align-items: center;
		gap: var(--padding-horizontal-s);
		margin-top: calc(var(--padding-vertical) * 2);

		.date-icon {
			width: 20px;
			height: 20px;

			&:deep(svg) {
				width: 100%;
				height: 100%;

				.fill {
					fill: var(--theme-txt2-color);
				}

				.stroke {
					stroke: var(--theme-txt2-color);
				}
			}
		}

		.date-row {
			display: flex;
			flex-direction: column;
			gap: 4px;

			.date-part, .time-part {
				color: var(--theme-txt2-color);
				font-size: var(--font-size);
				text-transform: capitalize;
			}
		}
	}

	.people-details {
		display: flex;
		align-items: center;
		gap: var(--padding-horizontal-s);
		margin-top: calc(var(--padding-vertical) * 2);

		.people-icon {
			width: 18px;
			height: 18px;

			&:deep(svg) {
				width: 100%;
				height: 100%;

				.fill {
					fill: var(--theme-txt2-color);
				}

				.stroke {
					stroke: var(--theme-txt2-color);
				}
			}
		}

		.people-row {
			display: flex;
			flex-direction: column;
			gap: 4px;

			.people-part {
				color: var(--theme-txt2-color);
				font-size: var(--font-size);
			}
		}
	}

	.actions-container {
		display: flex;
		justify-content: center;
		align-items: center;
		margin-top: calc(var(--padding-vertical) * 2);
		gap: var(--padding-horizontal);

		.menu-button {
			min-width: 250px;
		}

		@media (max-width: 768px) {
			flex-direction: column;
			gap: var(--padding-vertical);
		}
	}
}
</style>